/**
 * PaywallGate — renders children if user has sufficient tier,
 * otherwise shows a neutral access card.
 *
 * Doctrine: IN_APP_COPY.md — calm, no hype, no urgency.
 * "Competitive outcomes are unchanged."
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';

interface PaywallGateProps {
  requiredTier: 'pro' | 'coach_pro' | 'org_pro';
  title?: string;
  body?: string;
  children: React.ReactNode;
}

const DEFAULT_TITLES: Record<string, string> = {
  pro: 'Pro',
  coach_pro: 'Coach',
  org_pro: 'Club',
};

const DEFAULT_BODIES: Record<string, string> = {
  pro: 'Advanced performance insight for athletes who take competition seriously.',
  coach_pro: 'Professional tools for coaches who want their athletes\' results to speak for themselves.',
  org_pro: 'Competition infrastructure for gyms, schools, and teams that require trust at scale.',
};

export default function PaywallGate({ requiredTier, title, body, children }: PaywallGateProps) {
  const { tier } = useSubscription();
  const { theme } = useTheme();
  const router = useRouter();
  const c = theme.colors;

  if (hasAtLeast(tier, requiredTier)) {
    return <>{children}</>;
  }

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <Text style={[styles.title, { color: c.text }]}>
        {title ?? DEFAULT_TITLES[requiredTier] ?? 'Upgrade Required'}
      </Text>
      <Text style={[styles.body, { color: c.textSecondary }]}>
        {body ?? DEFAULT_BODIES[requiredTier] ?? 'This feature requires a higher subscription tier.'}
      </Text>
      <Text style={[styles.neutral, { color: c.textTertiary }]}>
        Competitive outcomes are unchanged.
      </Text>
      <TouchableOpacity
        style={[styles.button, { backgroundColor: c.accent }]}
        onPress={() => router.push('/paywall')}>
        <Text style={[styles.buttonText, { color: c.accentForeground }]}>View Plans</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 24,
    margin: 16,
    alignItems: 'center',
    gap: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
  },
  body: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  neutral: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 4,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 2,
    marginTop: 8,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '700',
  },
});
